import type { RemoteParticipant, Track, DisconnectReason } from 'livekit-client';

// ==================== 枚举 ====================

export enum RoomState {
  Standby = 'standby',
  Awakened = 'awakened',
  Listening = 'listening',
  Responding = 'responding',
}

export enum MicrophoneState {
  Idle = 'idle',
  Active = 'active',
  Error = 'error',
}

// ==================== 生命周期与日志 ====================

export type ClientLifecycle =
  | 'created'
  | 'connecting'
  | 'connected'
  | 'initializing'
  | 'ready'
  | 'reconnecting'
  | 'destroyed';

export type LogLevel = 'silent' | 'error' | 'warn' | 'info' | 'debug';

// ==================== 驱动配置 ====================

export interface BaseDriverConfig {
  token: string;
  rtc_addr: string;
  rtc_token: string;
}

export type DriverConfig = BaseDriverConfig;

// ==================== 应用信息 ====================

export interface AppInfo {
  app_id: number;
  tenant_id: number;
  app_name: string;
  app_key: string;
  web_rtc_service: string;
  asr_service: string;
  llm_service: string;
  tts_service: string;
  asr_service_mode: string;
}

// ==================== 项目数据 ====================

export interface ScriptItem {
  text: string;
  [key: string]: unknown;
}

export interface QAScriptItem {
  question: string;
  answer: string;
  [key: string]: unknown;
}

export interface PersonInfo {
  data_url?: string;
  speaker_id?: string;
  [key: string]: unknown;
}

export interface AudioSetting {
  audio_platform?: string;
  speed?: number;
  volume?: number;
  pitch?: number;
}

export interface TrackObject {
  x: number;
  y: number;
  width: number;
  height: number;
  r: number;
  url?: string;
  [key: string]: unknown;
}

export interface TrackItem {
  type: string;
  obj: TrackObject;
  z_index: number;
  [key: string]: unknown;
}

export interface SceneItem {
  tracks?: TrackItem[];
  audio?: AudioSetting;
  [key: string]: unknown;
}

export interface WorkspaceSchema {
  scene?: SceneItem[];
  screen_height?: number;
  screen_width?: number;
  [key: string]: unknown;
}

export interface DPData {
  id: string;
  wake_up?: boolean;
  interrupt?: boolean;
  wake_word?: string[];
  opening_remarks_script: ScriptItem[];
  conclusions_script: ScriptItem[];
  qa_script?: QAScriptItem[];
  person_info: PersonInfo;
  workspace: WorkspaceSchema;
  [key: string]: unknown;
}

// ==================== 绿幕抠像配置 ====================

export interface ChromaKeyConfig {
  /** Whether chroma key processing is enabled. Default false. */
  enabled?: boolean;
  /** Key color RGB (0–255). Default: green {r:0, g:255, b:0} */
  keyColor?: { r: number; g: number; b: number };
  /** RGB distance tolerance: pixels within this distance become fully transparent. 5–80, default 35 */
  tolerance?: number;
  /** Feather range for soft edges around the tolerance boundary. 0–40, default 12 */
  feather?: number;
  /** Edge shrink: extends the fully-transparent zone to eat into black fringing. 0–20, default 4 */
  shrink?: number;
  /** Alpha blur radius for smooth edges (GPU Gaussian / CPU box blur). 0–10, default 2 */
  softness?: number;
}

// ==================== 音频配置 ====================

export interface AudioConfig {
  sampleRate?: number;
  channelCount?: number;
  sampleSize?: number;
  echoCancellation?: boolean;
  noiseSuppression?: boolean;
  autoGainControl?: boolean;
}

// ==================== 消息格式 ====================

export interface DataChannelMessage {
  code: number;
  err_message: string;
  response_type: string;
  operate_type: string;
  data: string | {
    state?: string;
    text?: string;
    material_type?: string | null;
    material_data?: string;
    error_msg?: string;
    external_error_data?: {
      code: number;
      data?: string;
    };
    [key: string]: unknown;
  };
  timestamp: number | string;
  session_id?: string;
}

export interface ConversationMessage {
  type: 'user' | 'ai';
  content: string;
  timestamp: number;
  sessionId: string;
  operateType: 'update' | 'append' | 'finish';
}

// ==================== 错误数据 ====================

export type ReconnectFailReason =
  | 'KICKED'
  | 'ROOM_CLOSED'
  | 'DUPLICATE_SESSION'
  | 'AUTH_TOKEN_EXPIRED'
  | 'RTC_TOKEN_EXPIRED'
  | 'NETWORK_ERROR'
  | 'TIMEOUT'
  | 'MAX_ATTEMPTS'
  | 'DESTROYED'
  | 'UNKNOWN';

export interface RoomErrorData {
  message: string;
  source: 'connection' | 'api' | 'microphone' | 'backend' | 'driver';
  code: number;
  retriable?: boolean;
  phase?: 'api' | 'connect' | 'init' | 'runtime' | 'destroy' | 'reconnect';
  reason?: ReconnectFailReason;
}

// ==================== 重连配置 ====================

export interface ReconnectPolicy {
  /** 是否开启自动重连，默认 true */
  autoReconnect?: boolean;
  /**
   * 是否在连接彻底断开后自动执行完整重连
   * （重新获取 token 并重新加入房间）。
   * 设为 false 则只依赖 LiveKit 内置的快速恢复，
   * 彻底断开后不再尝试，仅通知调用方。
   * 默认 true。
   */
  fullReconnect?: boolean;
  /** 完整重连最大尝试次数，默认 3 */
  maxAttempts?: number;
  /** 重连基础延迟(ms)，使用指数退避，默认 2000 */
  baseDelayMs?: number;
  /** 重连最大延迟(ms)，默认 30000 */
  maxDelayMs?: number;
  /** 单次重连超时(ms)，默认 30000 */
  attemptTimeoutMs?: number;
  /** 获取新连接凭证的回调（手动 connect 模式必须提供；initWithTask 模式下 SDK 内部自动调用 enterRoom） */
  tokenProvider?: () => Promise<{ url: string; token: string }>;
}

export interface ReconnectAttemptInfo {
  attempt: number;
  maxAttempts: number;
  remaining: number;
  level: 'transport' | 'application';
}

export interface ReconnectSuccessInfo {
  level: 'transport' | 'application';
  attempts: number;
}

// ==================== 房间状态配置 ====================

export interface RoomStateConfig {
  name: string;
  description: string;
  state: RoomState;
}

// ==================== Init 消息 ====================

export interface InitMessageOptions {
  hotWords?: string[];
  faceDetection?: boolean;
  liveMode?: 'sdk' | 'normal' | 'preview';
}

export interface RoomInitMessage {
  cmd: 'init';
  req_id: string;
  data: {
    base: {
      live_mode: 'sdk' | 'normal' | 'preview';
      rtc_addr: string;
      rtc_token: string;
    };
    wake: {
      enable: boolean;
      keywords: string[];
      break_by_voice: boolean;
      to_greeting_timeout_seconds: number;
      to_greeting_words: string[];
      no_saying_greeting_timeout_seconds: number;
      no_saying_greeting_words: string[];
      no_saying_sleep_timeout_seconds: number;
    };
    asr: {
      client?: 'external' | 'local';
      external_url?: string;
      hot_words: string[];
    };
    llm: {
      client: string;
      external_url?: string;
      task_id: string;
    };
    tts: {
      external_url?: string;
      task_id: string;
    };
    auth: {
      public_key: string;
      token: string;
      mac: string;
      timestamp: number;
      salt_value: string;
      random_value: string;
      unique_id: string;
      encrypted_key: string;
    };
    person: {
      config: {
        h: number;
        w: number;
        fps: number;
        tracks: unknown[];
      };
    };
    multi_speaker: {
      enable: boolean;
    };
  };
}

// ==================== 性能监控 ====================

export interface VideoPerformanceStats {
  /** 视频解码帧率 (fps) */
  fps: number;
  /** 视频码率 (kbps) */
  bitrate: number;
  /** 视频宽度 (px) */
  width: number;
  /** 视频高度 (px) */
  height: number;
  /** 当前视频编解码器，如 "video/VP9" */
  codec: string | null;
  /** 累计已解码帧数 */
  framesDecoded: number;
  /** 累计丢帧数 */
  framesDropped: number;
  /** 累计接收帧数 */
  framesReceived: number;
  /** 接收抖动 (秒) */
  jitter: number;
  /** 累计丢包数 */
  packetsLost: number;
  /** 累计接收包数 */
  packetsReceived: number;
  /** 累计接收字节数 */
  bytesReceived: number;
  /** NACK 请求次数 */
  nackCount: number;
  /** PLI (Picture Loss Indication) 请求次数 */
  pliCount: number;
}

export interface AudioPerformanceStats {
  /** 音频码率 (kbps) */
  bitrate: number;
  /** 当前音频编解码器，如 "audio/opus" */
  codec: string | null;
  /** 接收抖动 (秒) */
  jitter: number;
  /** 累计丢包数 */
  packetsLost: number;
  /** 累计接收包数 */
  packetsReceived: number;
  /** 累计接收字节数 */
  bytesReceived: number;
}

export interface NetworkPerformanceStats {
  /** 往返时延 (ms) */
  rtt: number;
  /** ICE 候选类型: 'host' | 'srflx' | 'relay' | 'prflx' */
  candidateType: string;
}

export interface PerformanceStats {
  /** 采样时间戳 (ms, performance.now()) */
  timestamp: number;
  /** 视频性能指标，未接收视频时为 null */
  video: VideoPerformanceStats | null;
  /** 音频性能指标，未接收音频时为 null */
  audio: AudioPerformanceStats | null;
  /** 网络性能指标，无 PeerConnection 时为 null */
  network: NetworkPerformanceStats | null;
}

// ==================== SDK 配置 ====================

export interface DPRoomClientOptions {
  baseUrl?: string;
  accessToken?: string | (() => string);
  container?: string | HTMLElement;
  logLevel?: LogLevel;
  readyTimeoutMs?: number;
  readyTimeoutPolicy?: 'resolve' | 'reject';
  readyConditions?: {
    initAck?: boolean;
    videoTrack?: boolean;
  };
  connectPolicy?: 'ignore' | 'queue' | 'cancel-previous';
  requestPolicy?: {
    timeoutMs?: number;
    retry?: number;
    retryBackoffMs?: number;
    retryJitterMs?: number;
  };
  roomOptions?: {
    adaptiveStream?: boolean;
    dynacast?: boolean;
    singlePeerConnection?: boolean;
    autoSubscribe?: boolean;
    publishDefaults?: {
      videoCodec?: string;
      simulcast?: boolean;
      backupCodec?: boolean;
    };
  };
  liveMode?: 'normal' | 'preview';
  audioConfig?: AudioConfig;
  /** room-ready 后是否自动开启麦克风（用于唤醒词检测）。默认 true */
  autoEnableMicrophone?: boolean;
  chromaKey?: ChromaKeyConfig;
  reconnectPolicy?: ReconnectPolicy;
  onReady?: () => void;
  onStateChange?: (state: RoomState) => void;
  onError?: (error: RoomErrorData) => void;
}

// ==================== 事件类型映射 ====================

export interface DPRoomClientEvents {
  'lifecycle-change': (phase: ClientLifecycle, prevPhase: ClientLifecycle) => void;

  'connected': () => void;
  'disconnected': (reason?: DisconnectReason) => void;
  'abnormal-disconnect': (reason?: DisconnectReason) => void;

  'participant-connected': (participant: RemoteParticipant) => void;
  'participant-disconnected': (participant: RemoteParticipant) => void;

  'track-subscribed': (track: Track, participant: RemoteParticipant) => void;

  'room-ready': () => void;
  'driver-online': () => void;
  'driver-offline': () => void;
  'state-change': (newState: RoomState, oldState: RoomState) => void;

  'microphone-state-change': (state: MicrophoneState, prevState: MicrophoneState) => void;

  'data-received': (message: DataChannelMessage) => void;
  'asr-message': (message: ConversationMessage) => void;
  'llm-message': (message: ConversationMessage) => void;
  'session-end': () => void;

  'init-progress': (step: string, detail?: string) => void;

  'alpha-mode-change': (mode: 'native' | 'chroma-key') => void;

  'reconnecting': () => void;
  'reconnect-attempt': (info: ReconnectAttemptInfo) => void;
  'reconnected': (info: ReconnectSuccessInfo) => void;
  'reconnect-failed': (error: RoomErrorData) => void;

  'fps-update': (fps: number) => void;
  'performance-report': (stats: PerformanceStats) => void;

  'error': (error: RoomErrorData) => void;
}
